import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiCamera, HiVideoCamera, HiVideoCameraSlash, HiEye, HiEyeSlash } from 'react-icons/hi2';

const WebcamIndicator = ({ 
  isVisionEnabled = false, 
  onToggleVision, 
  visionAnalysis = null,
  showVideo = false 
}) => {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [webcamEnabled, setWebcamEnabled] = useState(true);
  const [showControls, setShowControls] = useState(false);

  useEffect(() => {
    if (showVideo && webcamEnabled && isVisionEnabled) {
      initializeWebcam();
    } else {
      stopWebcam();
    }

    return () => stopWebcam();
  }, [showVideo, webcamEnabled, isVisionEnabled]);

  const initializeWebcam = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: true,
        audio: false 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        setStream(mediaStream);
        setCameraActive(true);
      }
    } catch (error) {
      console.error('Error accessing webcam:', error);
      setCameraActive(false);
    }
  };

  const stopWebcam = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraActive(false);
  };

  const handleWebcamToggle = () => {
    const newWebcamState = !webcamEnabled;
    setWebcamEnabled(newWebcamState);
    
    if (!newWebcamState) {
      // If turning off webcam, also disable vision
      if (onToggleVision) {
        onToggleVision(false);
      }
    }
  };

  const handleVisionToggle = () => {
    if (onToggleVision && webcamEnabled) {
      onToggleVision(!isVisionEnabled);
    }
  };

  if (!showVideo) return null;

  const getStatusColor = () => {
    if (!webcamEnabled) return '#6b7280'; // Gray
    if (cameraActive && isVisionEnabled) return '#10b981'; // Green
    if (cameraActive) return '#f59e0b'; // Amber
    return '#ef4444'; // Red
  };

  const getStatusText = () => {
    if (!webcamEnabled) return 'Camera Off';
    if (cameraActive && isVisionEnabled) return 'Vision Active';
    if (cameraActive) return 'Camera Only';
    return 'Camera Error';
  };

  // Inline styles for reliable cross-environment rendering
  const containerStyle = {
    position: 'fixed',
    bottom: '20px',
    left: '20px',
    zIndex: 1000,
    pointerEvents: 'none'
  };

  const indicatorStyle = {
    pointerEvents: 'auto',
    position: 'relative'
  };

  const webcamCircleStyle = {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    overflow: 'hidden',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    background: 'rgba(0, 0, 0, 0.8)',
    position: 'relative',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
  };

  const webcamCircleHoverStyle = {
    ...webcamCircleStyle,
    borderColor: 'rgba(255, 255, 255, 0.5)',
    transform: 'scale(1.1)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.6)'
  };

  const videoStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transform: 'scaleX(-1)',
    borderRadius: '50%',
    display: webcamEnabled && cameraActive ? 'block' : 'none'
  };

  const cameraOffOverlayStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    background: 'rgba(0, 0, 0, 0.7)',
    borderRadius: '50%'
  };

  const statusIndicatorStyle = {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    border: '2px solid rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: getStatusColor()
  };

  const statusPulseStyle = {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: getStatusColor(),
    animation: 'pulse 2s infinite'
  };

  const controlsPanelStyle = {
    position: 'absolute',
    top: '-80px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(0, 0, 0, 0.9)',
    borderRadius: '12px',
    padding: '12px',
    minWidth: '160px',
    textAlign: 'center',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)'
  };

  const statusTextStyle = {
    color: 'white',
    fontSize: '12px',
    fontWeight: '600',
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const controlButtonsStyle = {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center'
  };

  const getControlBtnStyle = (isActive) => ({
    background: isActive ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.1)',
    border: isActive ? '1px solid #10b981' : '1px solid rgba(255, 255, 255, 0.2)',
    color: isActive ? '#10b981' : '#6b7280',
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px'
  });

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.7;
              transform: scale(1.2);
            }
          }
        `}
      </style>
      
      <motion.div
        style={indicatorStyle}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ duration: 0.3 }}
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(false)}
      >
        {/* Main Camera Display */}
        <div style={showControls ? webcamCircleHoverStyle : webcamCircleStyle}>
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={videoStyle}
          />
          
          {/* Camera Off Overlay */}
          {(!webcamEnabled || !cameraActive) && (
            <div style={cameraOffOverlayStyle}>
              {webcamEnabled ? (
                <HiCamera size={32} color="#6b7280" />
              ) : (
                <HiVideoCameraSlash size={32} color="#6b7280" />
              )}
            </div>
          )}

          {/* Status Indicator */}
          <div style={statusIndicatorStyle}>
            <div style={statusPulseStyle}></div>
          </div>
        </div>

        {/* Controls Panel */}
        <AnimatePresence>
          {showControls && (
            <motion.div
              style={controlsPanelStyle}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
            >
              <div style={statusTextStyle}>{getStatusText()}</div>
              
              <div style={controlButtonsStyle}>
                {/* Webcam Toggle */}
                <button
                  style={getControlBtnStyle(webcamEnabled)}
                  onClick={handleWebcamToggle}
                  title={webcamEnabled ? 'Turn off camera' : 'Turn on camera'}
                >
                  {webcamEnabled ? (
                    <HiVideoCamera size={16} />
                  ) : (
                    <HiVideoCameraSlash size={16} />
                  )}
                </button>

                {/* Vision Toggle */}
                <button
                  style={getControlBtnStyle(isVisionEnabled && webcamEnabled)}
                  onClick={handleVisionToggle}
                  disabled={!webcamEnabled}
                  title={isVisionEnabled ? 'Turn off vision analysis' : 'Turn on vision analysis'}
                >
                  {isVisionEnabled && webcamEnabled ? (
                    <HiEye size={16} />
                  ) : (
                    <HiEyeSlash size={16} />
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default WebcamIndicator; 